<template>
  <div
    class="sidebar-wrapper"
    :class="{ collapsed: isCollapsed, 'is-drawer': isDrawer }"
  >
    <div class="sidebar-content">
      <div class="sidebar-header">
        <img
          src="@/assets/images/apii-white-logo.svg"
          alt="APII"
          class="logo-image"
        />
        <button
          v-if="!isCollapsed"
          type="button"
          class="nav-toggle"
          title="Réduire le menu"
          @click="toggleMenu"
        >
          <i class="ri-side-bar-line"></i>
        </button>
      </div>

      <nav class="nav-menu">
        <div
          v-for="group in groupedMenuItems"
          :key="group.key"
          class="nav-group"
        >
          <div v-if="!isCollapsed" class="nav-group__label">
            {{ group.label }}
          </div>
          <div v-else class="nav-group__rule"></div>

          <button
            v-for="item in group.items"
            :key="item.id"
            type="button"
            class="nav-item"
            :class="{ 'nav-item--active': activeItem === item.id }"
            :title="isCollapsed ? item.text : ''"
            :aria-current="activeItem === item.id ? 'page' : null"
            @click="navigateTo(item)"
          >
            <span class="nav-item__icon">
              <i :class="['bi', 'bi-' + item.icon]"></i>
            </span>
            <span v-if="!isCollapsed" class="nav-item__text">{{
              item.text
            }}</span>
          </button>
        </div>
      </nav>

      <div class="sidebar-footer">
        <button
          type="button"
          class="nav-item nav-item--logout disconnect-btn"
          title="Déconnexion"
          @click="handleDisconnect"
        >
          <span class="nav-item__icon"
            ><i class="bi bi-box-arrow-right"></i
          ></span>
          <span v-if="!isCollapsed" class="nav-item__text">Déconnexion</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { menuItems, MENU_GROUPS } from '@/config/menuItems';
import { mapGetters } from 'vuex';
import { ROLES } from '@/constants/roles';

export default {
  name: 'SidebarMenu',
  data() {
    return {
      isCollapsed: false,
      // Sous 768px le rail sort du flux et s'ouvre par-dessus le contenu:
      // 248px de rail sur un ecran de 390px ne laissent pas de page.
      isDrawer: false,
      activeItem: 'dashboard',
      menuItems,
    };
  },
  computed: {
    ...mapGetters('auth', ['userRole']),
    resolvedMenuItems() {
      if (!this.userRole) {
        return [];
      }

      return this.menuItems.map((item) => {
        const resolvedLink = item.linksByRole
          ? (item.linksByRole[this.userRole] ??
            item.linksByRole.default ??
            null)
          : (item.link ?? null);

        return {
          ...item,
          link: resolvedLink,
        };
      });
    },
    /**
     * Filter menu items based on user role and resolved availability
     */
    filteredMenuItems() {
      if (!this.userRole) {
        return [];
      }

      if (this.userRole === ROLES.SUPER_ADMIN) {
        return this.resolvedMenuItems.filter((item) => item.link);
      }

      return this.resolvedMenuItems.filter((item) => {
        if (!item.link) {
          return false;
        }

        if (!item.roles || item.roles.length === 0) {
          return true;
        }

        return item.roles.includes(this.userRole);
      });
    },

    groupedMenuItems() {
      return MENU_GROUPS.map((group) => ({
        ...group,
        items: this.filteredMenuItems.filter(
          (item) => item.group === group.key,
        ),
      })).filter((group) => group.items.length);
    },
  },
  mounted() {
    // Detect active item based on current route
    this.updateActiveItem();
    this.applyViewport();
    window.addEventListener('resize', this.applyViewport);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.applyViewport);
    document.body.classList.remove('sidebar-drawer-open');
  },
  watch: {
    $route() {
      this.updateActiveItem();
      // Naviguer depuis le tiroir doit le refermer, sinon il masque la page
      // sur laquelle on vient d'arriver.
      if (this.isDrawer && !this.isCollapsed) {
        this.setCollapsed(true);
      }
    },
    userRole() {
      // Update active item when role changes
      this.updateActiveItem();
    },
  },
  methods: {
    updateActiveItem() {
      const currentPath = this.$route.path;
      const matchedItem = this.filteredMenuItems.find((item) => {
        if (!item.link) {
          return false;
        }

        return (
          currentPath === item.link || currentPath.startsWith(`${item.link}/`)
        );
      });
      if (matchedItem) {
        this.activeItem = matchedItem.id;
      }
    },
    navigateTo(item) {
      this.activeItem = item.id;

      // En tiroir, on referme sur le choix - y compris quand c'est la page
      // courante, ou aucune navigation n'a lieu et ou le tiroir resterait
      // ouvert sur ce qu'on venait de demander a voir.
      if (this.isDrawer) {
        this.setCollapsed(true);
      }

      if (item.link) {
        this.$router.push(item.link);
      }
    },
    setActive(id) {
      this.activeItem = id;
      this.$emit('navigate', id);
      console.log('Navigating to:', id);
    },
    /**
     * Le rail garde ses libelles jusqu'a la tablette incluse - a 768px il reste
     * 520px de contenu, ce qui suffit. Sous 768px il passe en tiroir, ferme au
     * depart: 248px de rail sur 390px d'ecran ne laissent pas de page.
     */
    applyViewport() {
      const drawer = window.innerWidth < 768;
      const entering = drawer && !this.isDrawer;

      this.isDrawer = drawer;

      if (entering && !this.isCollapsed) {
        this.setCollapsed(true);
      }
    },

    setCollapsed(collapsed) {
      this.isCollapsed = collapsed;
      this.$emit('toggle-menu', this.isCollapsed);

      if (this.isCollapsed) {
        document.body.classList.add('sidebar-collapsed');
        document.body.classList.remove('sidebar-drawer-open');
      } else {
        document.body.classList.remove('sidebar-collapsed');

        if (this.isDrawer) {
          document.body.classList.add('sidebar-drawer-open');
        }
      }
    },

    toggleMenu() {
      this.setCollapsed(!this.isCollapsed);
    },
    handleDisconnect() {
      console.log('Disconnect clicked');
      this.$emit('disconnect');
    },
  },
};
</script>

<style scoped>
/* Admin rail — Claude Design: 248px open, 76px collapsed, grouped items. */
.sidebar-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 248px;
  z-index: 10;
  transition: width 0.18s ease;
}

.sidebar-wrapper.collapsed {
  width: 76px;
}

.sidebar-content {
  width: 100%;
  height: 100%;
  background: #005bd0;
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ---- header ---- */
.sidebar-header {
  height: 72px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 0 52px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  box-sizing: border-box;
}

.collapsed .sidebar-header {
  padding: 0;
  justify-content: center;
}

.logo-image {
  height: 46px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.nav-toggle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.nav-toggle:hover {
  background: rgba(255, 255, 255, 0.12);
}

/* ---- groups ---- */
.nav-menu {
  flex: 1;
  padding: 14px 0 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden auto;
}

.nav-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 14px;
}

.nav-group__label {
  padding: 0 22px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #fff;
  white-space: nowrap;
}

/* Collapsed, a rule stands in for the group name. */
.nav-group__rule {
  height: 1px;
  background: rgba(255, 255, 255, 0.16);
  margin: 4px 16px 8px;
}

.nav-group:first-child .nav-group__rule {
  margin-top: 0;
}

/* ---- items ---- */
.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 42px;
  margin: 0 10px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  box-sizing: border-box;
  text-align: left;
}

.collapsed .nav-item {
  padding: 0;
  justify-content: center;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.nav-item--active,
.nav-item--active:hover {
  background: #fff;
  color: #005bd0;
  font-weight: 600;
}

.nav-item__icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 17px;
}

.nav-item__text {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---- footer ---- */
.sidebar-footer {
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  flex-shrink: 0;
}

.nav-item--logout {
  width: calc(100% - 20px);
  margin: 0 auto;
}

.collapsed .nav-item--logout {
  width: 100%;
}

/*
  Telephone: 248px de rail sur 390px d'ecran ne laissent pas de page. Le rail
  sort du flux et s'ouvre par-dessus le contenu, avec un fond qui le referme.
*/
@media (max-width: 767px) {
  .sidebar-wrapper.is-drawer {
    width: 248px;
    transform: translateX(-100%);
    transition:
      transform 0.18s ease,
      width 0.18s ease;
    z-index: 1050;
  }

  .sidebar-wrapper.is-drawer:not(.collapsed) {
    transform: translateX(0);
    box-shadow: 0 0 0 100vmax rgba(16, 24, 40, 0.45);
  }
}
</style>
